import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Table } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { useFormatters } from "../../engage/shared/format";
import { ServicePriorityBadge, ServiceStatusChip } from "../chips/service-chips";
import type { TicketSummary } from "./organisation-types";

/** A custom status carries its words per language (`{ en: "...", fil: "..." }`); the viewer's language, else English, else the category default. */
export function statusLabelOf(label: Readonly<Record<string, unknown>>, language: string): string | undefined {
  const preferred = label[language];
  if (typeof preferred === "string" && preferred) return preferred;
  const english = label.en;
  return typeof english === "string" && english ? english : undefined;
}

export function TicketStatus({ ticket }: { ticket: TicketSummary }) {
  const { i18n } = useTranslation();
  return <ServiceStatusChip category={ticket.status.category} label={statusLabelOf(ticket.status.label, i18n.language)} />;
}

export function TicketLink({ number }: { number: string }) {
  const { summary } = useWorkspaceContext();
  return (
    <AppLink href={consoleRoutes.tickets(summary.slug, number)} className="font-mono text-xs font-bold text-primary-ink hover:underline focus-visible:outline-2 focus-visible:outline-primary">
      {number}
    </AppLink>
  );
}

/** Ticket table (tablet and up) with a card list on a phone; used by the organisation's Tickets tab. */
export function TicketsTable({ tickets, label }: { tickets: readonly TicketSummary[]; label: string }) {
  const { t } = useTranslation();
  const format = useFormatters();
  return (
    <>
      <Table aria-label={label} containerClassName="hidden md:block">
        <thead>
          <tr>
            <th scope="col">{t("service.org.ticketsTab.ticket")}</th>
            <th scope="col">{t("service.org.ticketsTab.title")}</th>
            <th scope="col">{t("service.org.ticketsTab.requester")}</th>
            <th scope="col">{t("service.org.ticketsTab.status")}</th>
            <th scope="col">{t("service.org.ticketsTab.priority")}</th>
            <th scope="col">{t("service.org.ticketsTab.updated")}</th>
          </tr>
        </thead>
        <tbody>
          {tickets.map((ticket) => (
            <tr key={ticket.id} data-testid="organisation-ticket">
              <td><TicketLink number={ticket.number} /></td>
              <td className="max-w-72 truncate font-semibold">{ticket.title}</td>
              <td>{ticket.requester.name}</td>
              <td><TicketStatus ticket={ticket} /></td>
              <td><ServicePriorityBadge priority={ticket.priority} /></td>
              <td className="whitespace-nowrap text-fg-muted">{format.ago(ticket.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
      <ul aria-label={label} className="divide-y divide-line md:hidden">
        {tickets.map((ticket) => (
          <li key={ticket.id} className="space-y-2 p-4">
            <div className="flex items-center justify-between gap-2">
              <TicketLink number={ticket.number} />
              <span className="text-xs text-fg-muted">{format.ago(ticket.updated_at)}</span>
            </div>
            <p className="font-semibold">{ticket.title}</p>
            <div className="flex flex-wrap items-center gap-2">
              <TicketStatus ticket={ticket} />
              <ServicePriorityBadge priority={ticket.priority} />
            </div>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Compact rows (number, title, status): the overview's open tickets and the contact drawer. */
export function TicketMiniList({ tickets, label }: { tickets: readonly TicketSummary[]; label: string }) {
  return (
    <ul aria-label={label} className="space-y-2">
      {tickets.map((ticket) => (
        <li key={ticket.id} className="flex flex-wrap items-center justify-between gap-2">
          <span className="min-w-0 flex-1 truncate text-sm">
            <TicketLink number={ticket.number} /> <span className="font-semibold">{ticket.title}</span>
          </span>
          <TicketStatus ticket={ticket} />
        </li>
      ))}
    </ul>
  );
}
